import { useEffect, useRef, useState } from 'react';

import { getIconComponentByName } from './EntityTypeIcon';

/**
 * Curated list of business-relevant icons from @untitledui/icons.
 * These icons are commonly used for custom entity types, properties, and business objects.
 */
// eslint-disable-next-line react-refresh/only-export-components -- icon list constant co-located with IconPicker component
export const CURATED_ICONS = [
  'Folder',
  'User01',
  'Building07',
  'Target04',
  'Briefcase01',
  'ShoppingBag01',
  'Ticket01',
  'BookOpen01',
  'Flag01',
  'Heart',
  'Star01',
  'Zap',
  'Truck01',
  'Package',
  'Globe01',
  'Map01',
  'Tool01',
  'Clipboard',
  'FileText',
  'PieChart01',
  'BarChart01',
  'Layers01',
  'Grid01',
  'Tag01',
  'Award01',
  'Calendar',
  'Mail01',
  'Phone01',
  'CreditCard01',
];

interface IconPickerProps {
  /** Currently selected icon name */
  value: string | null;
  /** Callback when an icon is selected */
  onChange: (iconName: string) => void;
  /** Optional CSS class for the container */
  className?: string;
}

/**
 * IconPicker component that displays a curated grid of @untitledui/icons for selection.
 * Includes keyboard navigation, touch-friendly targets, and accessibility features.
 */
export function IconPicker({
  value,
  onChange,
  className = '',
}: IconPickerProps) {
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const gridRef = useRef<HTMLDivElement>(null);

  // Auto-focus first item when component mounts if no value selected,
  // or if value is non-null but not in the curated list (unknown icon)
  useEffect(() => {
    if (focusedIndex === -1) {
      if (value === null || !CURATED_ICONS.includes(value)) {
        setFocusedIndex(0);
      }
    }
  }, [value, focusedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Detect column count from actual grid layout (4 on mobile, 6 on sm+)
    const cols = window.matchMedia('(min-width: 640px)').matches ? 6 : 4;
    const _rows = Math.ceil(CURATED_ICONS.length / cols);
    let newIndex = focusedIndex;

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        newIndex = Math.min(focusedIndex + 1, CURATED_ICONS.length - 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        newIndex = Math.max(focusedIndex - 1, 0);
        break;
      case 'ArrowDown':
        e.preventDefault();
        newIndex = Math.min(focusedIndex + cols, CURATED_ICONS.length - 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        newIndex = Math.max(focusedIndex - cols, 0);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < CURATED_ICONS.length) {
          onChange(CURATED_ICONS[focusedIndex]);
        }
        return;
      default:
        return;
    }

    setFocusedIndex(newIndex);
    // Focus the button at the new index
    const buttons = gridRef.current?.querySelectorAll('button');
    if (buttons && buttons[newIndex]) {
      (buttons[newIndex] as HTMLButtonElement).focus();
    }
  };

  return (
    <div className={className}>
      <label className='block text-sm font-medium mb-3 text-gray-900 dark:text-gray-100'>
        Choose an icon
      </label>
      <div
        ref={gridRef}
        role='radiogroup'
        aria-label='Icon selection'
        className='grid grid-cols-4 sm:grid-cols-6 gap-2'
        onKeyDown={handleKeyDown}
      >
        {CURATED_ICONS.map((iconName, index) => {
          const IconComponent = getIconComponentByName(iconName);
          const isSelected = value === iconName;
          const isFocused = focusedIndex === index;

          return (
            <button
              key={iconName}
              type='button'
              role='radio'
              aria-checked={isSelected}
              aria-label={iconName}
              tabIndex={
                isFocused || (isSelected && focusedIndex === -1) ? 0 : -1
              }
              onClick={() => onChange(iconName)}
              onFocus={() => setFocusedIndex(index)}
              className={`
                flex flex-col items-center justify-center p-2 rounded-lg
                transition duration-100 ease-linear
                min-h-[44px] min-w-[44px]
                ${
                  isSelected
                    ? 'bg-utility-brand-50 border-2 border-utility-brand-600'
                    : 'border-2 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800'
                }
                outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2
              `}
            >
              <IconComponent className='size-5 text-gray-600 dark:text-gray-400' />
              <span className='text-xs mt-1 truncate w-full text-center text-gray-500 dark:text-gray-500'>
                {iconName}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
